import { useState } from "react";
import { Sidebar } from "primereact/sidebar";
import { Tooltip } from "primereact/tooltip";
import { FiEdit2, FiAward, FiStar } from "react-icons/fi";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import FormField from "components/v2/FormField";
import RichTextEditor from "components/RichTextEditor";
import useEditBook from "api/BookResource/useEditBook";
import useSetPickOfTheWeek from "api/BookResource/useSetPickOfTheWeek";
import useSetRecommended from "api/BookResource/useSetRecommended";
import useAuthStore from "store/authStore";
import { Role } from "utils/server-constants/roles";
import { BookFormat } from "utils/server-constants/bookFormats";
import { MAX_IMAGE_SIZE, MAX_PDF_SIZE, activeCurrencies } from "utils";
import DeleteBook from "./BookCard/DeleteBook";
import Details from "./BookCard/Details";
import { IoIosArrowForward } from "react-icons/io";

const BookActionsColumn = ({ book, categories, genres, isAdmin }) => {
	const [visible, setVisible] = useState(false);
	const [showEditBook, setShowEditBook] = useState(false);
	const [showCoverField, setShowCoverField] = useState(false);
	const [showFileField, setShowFileField] = useState(false);

	const hasRoles = useAuthStore((state) => state.hasRoles);

	const { mutateAsync: mutatePickOfTheWeek, isLoading: isSettingPickOfTheWeek } =
		useSetPickOfTheWeek(book?.id);

	const { mutateAsync: mutateSetRecommended, isLoading: isSettingRecommended } =
		useSetRecommended(book?.id);

	const { mutateAsync, isLoading } = useEditBook(book?.id, setShowEditBook);

	const setAsPickofTheWeek = async (e) => {
		e.stopPropagation();
		await mutatePickOfTheWeek();
	};

	const setAsRecommended = async (e) => {
		e.stopPropagation();
		await mutateSetRecommended();
	};

	const initialValues = {
		title: book?.title || "",
		isbn: book?.isbn || "",
		author: book?.author || "",
		book_cover: null,
		book_file: null,
		price: book?.price || "",
		zwl_price: book?.zwl_price || "",
		description: book?.description || "",
		genres: book?.genres?.[0]?.id || "",
		category_id: book?.category?.id || "",
		subscription: Boolean(book?.subscription),
		sellable: Boolean(book?.sellable),
		isFree: Boolean(book?.isFree),
		format: book?.format || "",
		stock_level: book?.stock_level || "",
		physical_usd_price: book?.physical_usd_price || "",
		physical_zwl_price: book?.physical_zwl_price || "",
	};

	const validate = Yup.object().shape({
		title: Yup.string().required("Book title is required"),
		author: Yup.string().required("Author is required"),
		price: Yup.number().when("isFree", {
			is: true,
			then: Yup.number().notRequired(),
			otherwise: Yup.number()
				.required("Price is required")
				.moreThan(0, "Price should be greater than 0"),
		}),
		description: Yup.mixed(),
		category_id: Yup.number().required("Book category is required"),
		genres: Yup.string().required("Book genre is required"),
		book_cover: Yup.mixed()
			.test({
				name: "fileSize",
				test: (value) => !value || value.size / 1024 / 1024 <= MAX_IMAGE_SIZE,
				message: `Image size should not exceed ${MAX_IMAGE_SIZE}MB`,
			})
			.test({
				name: "mimeType",
				test: (value) =>
					!value || ["image/jpeg", "image/png", "image/svg+xml", "image/webp"].includes(value.type),
				message: "Only images with these file extension are required: jpeg, jpg, png, svg, webp",
			}),
		book_file: Yup.mixed()
			.test({
				name: "fileSize",
				test: (value) => !value || value.size / 1024 / 1024 <= MAX_PDF_SIZE,
				message: `File size should not exceed ${MAX_PDF_SIZE}MB`,
			})
			.test({
				name: "mineType",
				test: (value) => !value || ["application/pdf", "application/epub+zip"].includes(value.type),
				message: "Only Pdf or Epub documents are required",
			}),
		format: hasRoles([Role.SuperAdmin, Role.Admin])
			? Yup.string().required("Book format is required")
			: Yup.mixed().notRequired(),
		stock_level: hasRoles([Role.SuperAdmin, Role.Admin])
			? Yup.mixed().when("format", {
					is: (format) => ["physical", "both"].includes(format),
					then: Yup.number()
						.typeError("Stock level must be a number")
						.required("Stock level is required"),
					otherwise: Yup.mixed().notRequired(),
			  })
			: Yup.mixed().notRequired(),
		physical_usd_price: hasRoles([Role.SuperAdmin, Role.Admin])
			? Yup.mixed().when("format", {
					is: (format) => [BookFormat.Physical, BookFormat.Both].includes(format),
					then: Yup.number()
						.typeError("Price must be a number")
						.required("Set price of the physical book"),
					otherwise: Yup.mixed().notRequired(),
			  })
			: Yup.mixed().notRequired(),
		physical_zwl_price: hasRoles([Role.SuperAdmin, Role.Admin])
			? Yup.mixed().when("format", {
					is: (format) => [BookFormat.Physical, BookFormat.Both].includes(format),
					then: Yup.number()
						.typeError("Price must be a number")
						.required("Set price of the physical book"),
					otherwise: Yup.mixed().notRequired(),
			  })
			: Yup.mixed().notRequired(),
	});

	const handleSubmit = async (data) => {
		await mutateAsync({
			...data,
			is_active: data.is_active ? 1 : 0,
			subscription: data.subscription ? 1 : 0,
			sellable: data.sellable ? 1 : 0,
			isFree: data.isFree ? 1 : 0,
		});
		setShowEditBook(false);
	};

	return (
		<>
			<div className="flex items-center justify-center gap-1">
				<Tooltip target=".action-btn" position="top" />
				
				<button
					type="button"
					onClick={() => setVisible(true)}
					className="action-btn w-8 h-8 rounded-lg flex items-center justify-center bg-akgreen/10 text-akgreener hover:bg-akgreen/20 transition-all"
					data-pr-tooltip="Edit Book"
				>
					<FiEdit2 size={14} />
				</button>

				{isAdmin && (
					<>
						<button
							type="button"
							onClick={setAsPickofTheWeek}
							className={`action-btn w-8 h-8 rounded-lg flex items-center justify-center transition-all ${
								book?.pick_of_week
									? "bg-orangelib/10 text-orangelib"
									: "bg-gray-100 text-gray-400 hover:text-orangelib hover:bg-orangelib/10"
							}`}
							data-pr-tooltip={book?.pick_of_week ? "Remove from Pick of Week" : "Set as Pick of Week"}
						>
							{isSettingPickOfTheWeek ? (
								<AiOutlineLoading3Quarters size={14} className="animate-spin" />
							) : (
								<FiAward size={14} fill={book?.pick_of_week ? "currentColor" : "none"} />
							)}
						</button>

						<button
							type="button"
							onClick={setAsRecommended}
							className={`action-btn w-8 h-8 rounded-lg flex items-center justify-center transition-all ${
								book?.recommended
									? "bg-akgreen/10 text-akgreener"
									: "bg-gray-100 text-gray-400 hover:text-akgreen hover:bg-akgreen/10"
							}`}
							data-pr-tooltip={book?.recommended ? "Remove Recommendation" : "Recommend"}
						>
							{isSettingRecommended ? (
								<AiOutlineLoading3Quarters size={14} className="animate-spin" />
							) : (
								<FiStar size={14} fill={book?.recommended ? "currentColor" : "none"} />
							)}
						</button>

						<DeleteBook elementId={`delete-${book?.id}`} bookId={book?.id} />
					</>
				)}
			</div>

			<Sidebar
				visible={visible}
				style={{ minWidth: "700px", maxWidth: "800px" }}
				onHide={() => setVisible(false)}
				position="right"
				blockScroll
				className="p-sidebar-md"
			>
				<div className="space-y-10 px-4">
					{!showEditBook && (
						<div>
							<Details
								title={book?.title}
								bookCover={book?.book_cover?.url}
								author={book?.author}
								publisher={book?.publisher}
								category={book?.category?.name}
								genre={book?.genres?.[0]?.name}
								usdPrice={book?.price}
								zwlPrice={book?.zwl_price}
								description={book?.description}
								isbn={book?.isbn}
								physicalUsdPrice={book?.physical_usd_price}
								physicalZwlPrice={book?.physical_zwl_price}
							/>
						</div>
					)}

					<div>
						<button
							type="button"
							onClick={() => setShowEditBook(!showEditBook)}
							className="bg-akgreener hover:bg-akgreenest rounded-full py-2.5 px-6 text-white text-sm font-bold shadow-sm hover:shadow-md transition-all active:scale-95"
						>
							{showEditBook ? "Show book details" : "Edit book"}
						</button>
					</div>

					{showEditBook && (
						<Formik
							initialValues={initialValues}
							validationSchema={validate}
							onSubmit={handleSubmit}
						>
							{({ setFieldValue, values, errors, touched }) => {
								const hasPhysical = [BookFormat.Physical, BookFormat.Both].includes(
									values?.format || ""
								);
								return (
									<Form className="space-y-6">
										<div className="flex gap-3">
											<div className="w-1/2">
												<FormField type="text" name="title" label="Title *" />
											</div>
											<div className="w-1/2">
												<FormField type="text" name="isbn" label="ISBN Number" />
											</div>
										</div>

										<div className="w-full">
											<FormField type="text" name="author" label="Author Name *" />
										</div>

										<div className="flex gap-3">
											<div className="w-1/2">
												{book?.book_cover?.url && !showCoverField ? (
													<div className="flex flex-col justify-center mb-2">
														<span className="text-sm font-medium text-gray-500">Book already has a cover</span>
														<button
															type="button"
															onClick={() => setShowCoverField(true)}
															className="text-akgreener text-sm font-bold max-w-max hover:text-akgreenest transition-colors mt-1"
														>
															Update cover
														</button>
													</div>
												) : (
													<FormField
														type="file"
														name="book_cover"
														value={null}
														label="Book Cover"
														style={{
															paddingLeft: `${!values?.book_cover ? "90px" : ""} `,
														}}
														onChange={(event) => setFieldValue("book_cover", event.target.files[0])}
													/>
												)}
											</div>
											<div className="w-1/2">
												{book?.has_book_file && !showFileField ? (
													<div className="flex flex-col justify-center mb-2">
														<span className="text-sm font-medium text-gray-500">Book already has a file</span>
														<button
															type="button"
															onClick={() => setShowFileField(true)}
															className="text-akgreener text-sm font-bold max-w-max hover:text-akgreenest transition-colors mt-1"
														>
															Update book file (pdf/epub)
														</button>
													</div>
												) : (
													<FormField
														type="file"
														name="book_file"
														value={null}
														label="Book File"
														style={{
															paddingLeft: `${!values?.book_file ? "90px" : ""} `,
														}}
														onChange={(event) => setFieldValue("book_file", event.target.files[0])}
													/>
												)}
											</div>
										</div>

										{hasRoles([Role.SuperAdmin, Role.Admin]) && (
											<div className={hasPhysical ? "flex flex-row gap-2" : "flex flex-col gap-6"}>
												<div className={hasPhysical ? "w-1/2" : "w-full"}>
													<FormField
														type="select"
														name="format"
														label="Book format"
														options={[
															{
																label: "Digital copies (for online reading)",
																value: "digital",
															},
															{
																label: "Physical copies",
																value: "physical",
															},
															{
																label: "Both physical and digital copies",
																value: "both",
															},
														].map((option) => ({
															label: option.label,
															value: option.value,
														}))}
													/>
												</div>
												<div className={hasPhysical ? "w-1/2" : "w-full hidden"}>
													<FormField
														type="number"
														min="0"
														step="0.01"
														name="stock_level"
														label="Number of physical copies available"
													/>
												</div>
											</div>
										)}

										<div className="flex gap-2">
											<div className="w-1/2">
												<FormField
													type="number"
													min="0"
													step="0.01"
													name="price"
													label={`Price (${activeCurrencies.USD.label})`}
												/>
											</div>
											<div className="w-1/2">
												<FormField
													type="number"
													min="0"
													step="0.01"
													name="zwl_price"
													label={`Price (${activeCurrencies.ZWL.label})`}
												/>
											</div>
										</div>

										{hasRoles([Role.SuperAdmin, Role.Admin]) && (
											<div className={` ${hasPhysical ? "flex gap-2" : "hidden"}`}>
												<div className="w-1/2">
													<FormField
														type="number"
														min="0"
														step="0.01"
														name="physical_usd_price"
														label={`Physical book price (${activeCurrencies.USD.label})`}
													/>
												</div>
												<div className="w-1/2">
													<FormField
														type="number"
														min="0"
														step="0.01"
														name="physical_zwl_price"
														label={`Physical book price (${activeCurrencies.ZWL.label})`}
													/>
												</div>
											</div>
										)}

										<div className="flex gap-2">
											<div className="w-1/2">
												<FormField
													type="select"
													name="category_id"
													label="Book category"
													options={categories?.data?.map((category) => ({
														label: category.name,
														value: category.id,
													}))}
												/>
											</div>
											<div className="w-1/2">
												<FormField
													type="select"
													name="genres"
													label="Book Genre"
													options={genres?.data?.data?.map((genre) => ({
														label: genre.name,
														value: genre.id,
													}))}
												/>
											</div>
										</div>

										<div className="space-y-2">
											<p className="text-gray-500">Book summary</p>
											<RichTextEditor
												value={values.description}
												onTextChange={(e) => setFieldValue("description", e.htmlValue)}
												error={!!(touched.description && errors.description) && errors.description}
											/>
										</div>

										<div className="space-y-2">
											<FormField
												type="switcher"
												name="subscription"
												label="Do you want people to subscribe to this book?"
											/>

											<FormField
												type="switcher"
												label="Can people buy this book?"
												name="sellable"
											/>

											<FormField type="switcher" label="Make the book free?" name="isFree" />
										</div>

										<div className="flex justify-center border-t border-gray-300 pt-4 mt-6">
											<button
												type="submit"
												className="px-8 py-3 rounded-full bg-akgreenest hover:bg-black text-white font-bold flex gap-2 items-center justify-center shadow-md hover:shadow-lg transition-all active:scale-95"
											>
												{isLoading ? (
													<>
														Updating{" "}
														<AiOutlineLoading3Quarters size={20} className="animate-spin" />
													</>
												) : (
													<>
														Update Book <IoIosArrowForward size={20} />
													</>
												)}
											</button>
										</div>
									</Form>
								);
							}}
						</Formik>
					)}
				</div>
			</Sidebar>
		</>
	);
};

export default BookActionsColumn;
